// services/video.js
import api from './api';

const videoService = {
  /**
   * Récupère toutes les vidéos (feed principal)
   * @param {Object} params - Paramètres de filtrage
   * @param {number} params.user_id - ID de l'utilisateur connecté
   * @param {string} params.type - Type de vidéos ('all', 'matches', 'following')
   * @param {number} params.page - Numéro de page pour pagination
   * @param {number} params.limit - Nombre de vidéos par page
   * @returns {Promise<{success: boolean, videos: Array, total: number, error?: string}>}
   */
  getVideos: async (params = {}) => {
    try {
      const response = await api.get('/get-videos.php', {
        params: {
          user_id: params.user_id,
          type: params.type || 'all',
          page: params.page || 1,
          limit: params.limit || 20,
          _t: Date.now() // Cache buster
        }
      });

      if (response.data.success) {
        return {
          success: true,
          videos: response.data.videos || [],
          total: response.data.total || 0,
          page: response.data.page || 1,
          hasMore: response.data.hasMore || false
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors du chargement des vidéos'
        };
      }
    } catch (error) {
      console.error('❌ getVideos error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * Récupère les vidéos d'un utilisateur spécifique
   * @param {number} userId - ID de l'utilisateur
   * @param {number} currentUserId - ID de l'utilisateur connecté (pour vérifier les accès)
   * @returns {Promise<{success: boolean, videos: Array, error?: string}>}
   */
  getUserVideos: async (userId, currentUserId = null) => {
    if (!userId) {
      return { success: false, error: 'ID utilisateur requis' };
    }

    try {
      const response = await api.get('/get-user-videos.php', {
        params: {
          user_id: userId,
          current_user_id: currentUserId,
          _t: Date.now()
        }
      });

      if (response.data.success) {
        return {
          success: true,
          videos: response.data.videos || [],
          total: response.data.total || 0
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors du chargement des vidéos'
        };
      }
    } catch (error) {
      console.error('❌ getUserVideos error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * Récupère une vidéo spécifique
   * @param {number} videoId - ID de la vidéo
   * @param {number} currentUserId - ID de l'utilisateur connecté
   * @returns {Promise<{success: boolean, video?: Object, error?: string}>}
   */
  getVideo: async (videoId, currentUserId = null) => {
    if (!videoId) {
      return { success: false, error: 'ID vidéo requis' };
    }

    try {
      const response = await api.get('/get-video.php', {
        params: {
          video_id: videoId,
          current_user_id: currentUserId,
          _t: Date.now()
        }
      });

      if (response.data.success) {
        return {
          success: true,
          video: response.data.video
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors du chargement de la vidéo'
        };
      }
    } catch (error) {
      console.error('❌ getVideo error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * Like ou unlike une vidéo
   * @param {number} videoId - ID de la vidéo
   * @param {number} userId - ID de l'utilisateur qui like
   * @returns {Promise<{success: boolean, action: 'liked'|'unliked', likes: number, error?: string}>}
   */
  likeVideo: async (videoId, userId) => {
    if (!videoId || !userId) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const response = await api.post('/like-video.php', {
        video_id: videoId,
        user_id: userId
      });

      if (response.data.success) {
        return {
          success: true,
          action: response.data.action, // 'liked' ou 'unliked'
          likes: response.data.likes_count, // Nouveau total de likes
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors du like'
        };
      }
    } catch (error) {
      console.error('❌ likeVideo error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * Incrémente le compteur de vues d'une vidéo
   * @param {number} videoId - ID de la vidéo
   * @param {number} userId - ID de l'utilisateur qui regarde
   * @returns {Promise<{success: boolean, views?: number, error?: string}>}
   */
  incrementViews: async (videoId, userId = null) => {
    if (!videoId) {
      return { success: false, error: 'ID vidéo requis' };
    }

    try {
      const response = await api.post('/increment-views.php', {
        video_id: videoId,
        user_id: userId
      });

      if (response.data.success) {
        return {
          success: true,
          views: response.data.views_count,
          message: 'Vue comptabilisée'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors de l\'incrémentation'
        };
      }
    } catch (error) {
      console.error('❌ incrementViews error:', error);
      // On ne bloque pas l'expérience utilisateur pour une erreur de vue
      return {
        success: false,
        error: error.response?.data?.error
      };
    }
  },

  /**
   * Vérifie si l'utilisateur a accès à une vidéo privée
   * @param {number} videoId - ID de la vidéo
   * @param {number} userId - ID de l'utilisateur
   * @returns {Promise<{success: boolean, hasAccess: boolean, reason?: string, error?: string}>}
   */
  checkAccess: async (videoId, userId) => {
    if (!videoId || !userId) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const response = await api.get('/check-video-access.php', {
        params: {
          video_id: videoId,
          user_id: userId,
          _t: Date.now()
        }
      });

      if (response.data.success) {
        return {
          success: true,
          hasAccess: response.data.hasAccess,
          reason: response.data.reason // Pourquoi l'accès est refusé (privé, pas match, etc.)
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors de la vérification'
        };
      }
    } catch (error) {
      console.error('❌ checkAccess error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  },

  /**
   * Supprime une vidéo
   * @param {number} videoId - ID de la vidéo
   * @param {number} userId - ID de l'utilisateur (propriétaire)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  deleteVideo: async (videoId, userId) => {
    if (!videoId || !userId) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const response = await api.post('/delete-video.php', {
        video_id: videoId,
        user_id: userId
      });

      if (response.data.success) {
        return {
          success: true,
          message: 'Vidéo supprimée avec succès'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors de la suppression'
        };
      }
    } catch (error) {
      console.error('❌ deleteVideo error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupère les commentaires d'une vidéo
   * @param {number} videoId - ID de la vidéo
   * @param {Object} params - Paramètres de pagination
   * @returns {Promise<{success: boolean, comments: Array, error?: string}>}
   */
  getComments: async (videoId, params = {}) => {
    if (!videoId) {
      return { success: false, error: 'ID vidéo requis' };
    }

    try {
      const response = await api.get('/get-video-comments.php', {
        params: {
          video_id: videoId,
          page: params.page || 1,
          limit: params.limit || 20,
          _t: Date.now()
        }
      });

      if (response.data.success) {
        return {
          success: true,
          comments: response.data.comments || [],
          total: response.data.total || 0,
          hasMore: response.data.hasMore || false
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors du chargement des commentaires'
        };
      }
    } catch (error) {
      console.error('❌ getComments error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  },

  /**
   * Ajoute un commentaire à une vidéo
   * @param {Object} data - Données du commentaire
   * @param {number} data.video_id - ID de la vidéo
   * @param {number} data.user_id - ID de l'utilisateur
   * @param {string} data.comment - Contenu du commentaire
   * @returns {Promise<{success: boolean, comment?: Object, error?: string}>}
   */
  addComment: async (data) => {
    if (!data.video_id || !data.user_id || !data.comment?.trim()) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const response = await api.post('/add-video-comment.php', {
        video_id: data.video_id,
        user_id: data.user_id,
        comment: data.comment.trim()
      });

      if (response.data.success) {
        return {
          success: true,
          comment: response.data.comment,
          message: 'Commentaire ajouté'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors de l\'ajout'
        };
      }
    } catch (error) {
      console.error('❌ addComment error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  },

  /**
   * Signale une vidéo
   * @param {number} videoId - ID de la vidéo
   * @param {number} userId - ID de l'utilisateur qui signale
   * @param {string} reason - Motif du signalement
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  reportVideo: async (videoId, userId, reason) => {
    if (!videoId || !userId || !reason?.trim()) {
      return { success: false, error: 'Paramètres manquants' };
    }

    try {
      const response = await api.post('/report-video.php', {
        video_id: videoId,
        user_id: userId,
        reason: reason.trim()
      });

      if (response.data.success) {
        return {
          success: true,
          message: 'Vidéo signalée, merci de votre vigilance'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Erreur lors du signalement'
        };
      }
    } catch (error) {
      console.error('❌ reportVideo error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  }
};

export default videoService;